import { Injectable } from '@angular/core';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { FirebaseApp, getApp, getApps, initializeApp } from 'firebase/app';
import {
  Auth,
  AuthCredential,
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  connectAuthEmulator,
  getAuth,
  GoogleAuthProvider,
  linkWithCredential,
  OAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  verifyPasswordResetCode
} from 'firebase/auth';
import { environment } from 'src/environments/environment';
import { isNativeApp } from 'src/app/util/app-platform';

const FIREBASE_APP_NAME = 'lad-patient-identity';
export type IdentityPlatformSocialMethod = 'google' | 'apple' | 'microsoft';

// The environment files are plain object literals with slightly different
// shapes (the local environment has no tenant), so read the config through a
// typed view instead of relying on each file's inferred type.
export interface IdentityPlatformEnvironmentConfig {
  apiKey?: string;
  authDomain?: string;
  projectId?: string;
  appId?: string;
  tenantId?: string;
  authEmulatorUrl?: string;
}

interface IdentityPlatformAppConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  appId: string;
}

const REQUIRED_IDENTITY_PLATFORM_FIELDS: Array<keyof IdentityPlatformAppConfig> = ['apiKey', 'authDomain', 'projectId', 'appId'];

export function validateIdentityPlatformConfiguration(config: IdentityPlatformEnvironmentConfig | undefined, production: boolean): void {
  const hasConfiguration = !!config && Object.values(config).some((value) => !!value);
  if (!hasConfiguration && !production) return;
  if (!config) throw new Error('Identity Platform configuration is missing.');

  for (const field of REQUIRED_IDENTITY_PLATFORM_FIELDS) {
    const value = config[field]?.trim();
    if (!value || value.startsWith('$IDENTITY_PLATFORM_')) {
      throw new Error(`Identity Platform configuration field ${field} is missing or was not substituted.`);
    }
  }

  if (config.authEmulatorUrl) {
    if (production) throw new Error('The Identity Platform Auth emulator cannot be used in production.');
  } else {
    const tenantId = config.tenantId?.trim();
    if (!tenantId || tenantId.startsWith('$IDENTITY_PLATFORM_')) {
      throw new Error('Identity Platform tenantId is required outside the local Auth emulator.');
    }
  }
}

@Injectable({
  providedIn: 'root'
})
export class IdentityPlatformAuthenticationService {
  private auth?: Auth;
  private initialization?: Promise<void>;
  private nativeSession = false;
  private pendingSocialCredential?: AuthCredential;
  private pendingSocialMethod?: IdentityPlatformSocialMethod;

  validateConfiguration(): void {
    validateIdentityPlatformConfiguration(
      environment.IDENTITY_PLATFORM as IdentityPlatformEnvironmentConfig | undefined,
      environment.production === true
    );
  }

  initialize(): Promise<void> {
    if (!this.initialization) {
      this.initialization = this.initializeSession();
    }
    return this.initialization;
  }

  hasSession(): boolean {
    return isNativeApp() ? this.nativeSession : !!this.auth?.currentUser;
  }

  async getIdToken(forceRefresh = false): Promise<string | null> {
    await this.initialize();

    if (isNativeApp()) {
      if (!this.nativeSession) return null;
      const result = await FirebaseAuthentication.getIdToken({ forceRefresh });
      return result.token;
    }

    return this.auth?.currentUser?.getIdToken(forceRefresh) || null;
  }

  async signInWithEmailAndPassword(email: string, password: string): Promise<void> {
    await this.initialize();

    if (isNativeApp()) {
      await FirebaseAuthentication.signInWithEmailAndPassword({ email, password });
      this.nativeSession = true;
      return;
    }

    await signInWithEmailAndPassword(this.requireWebAuth(), email, password);
  }

  async createUserWithEmailAndPassword(email: string, password: string): Promise<void> {
    await this.initialize();

    if (isNativeApp()) {
      await FirebaseAuthentication.createUserWithEmailAndPassword({ email, password });
      this.nativeSession = true;
      return;
    }

    await createUserWithEmailAndPassword(this.requireWebAuth(), email, password);
  }

  async signInWithSocial(method: IdentityPlatformSocialMethod): Promise<void> {
    await this.initialize();

    try {
      if (isNativeApp()) {
        if (method === 'google') await FirebaseAuthentication.signInWithGoogle();
        if (method === 'apple') await FirebaseAuthentication.signInWithApple();
        if (method === 'microsoft') await FirebaseAuthentication.signInWithMicrosoft();
        this.nativeSession = true;
        return;
      }

      const provider =
        method === 'google' ? new GoogleAuthProvider() : new OAuthProvider(method === 'apple' ? 'apple.com' : 'microsoft.com');
      provider.setCustomParameters({ prompt: 'select_account' });
      await signInWithPopup(this.requireWebAuth(), provider);
    } catch (error: any) {
      if (error?.code === 'auth/account-exists-with-different-credential') {
        this.pendingSocialMethod = method;
        if (!isNativeApp()) {
          this.pendingSocialCredential =
            method === 'google'
              ? GoogleAuthProvider.credentialFromError(error) || undefined
              : OAuthProvider.credentialFromError(error) || undefined;
        }
      }
      throw error;
    }
  }

  hasPendingSocialCredential(): boolean {
    return !!this.pendingSocialMethod;
  }

  async linkPendingSocialCredential(): Promise<void> {
    if (!this.pendingSocialMethod) return;
    await this.initialize();

    if (isNativeApp()) {
      if (this.pendingSocialMethod === 'google') await FirebaseAuthentication.linkWithGoogle();
      if (this.pendingSocialMethod === 'apple') await FirebaseAuthentication.linkWithApple();
      if (this.pendingSocialMethod === 'microsoft') await FirebaseAuthentication.linkWithMicrosoft();
    } else {
      const user = this.requireWebAuth().currentUser;
      if (!user || !this.pendingSocialCredential) {
        throw new Error('The pending social credential is no longer available.');
      }
      await linkWithCredential(user, this.pendingSocialCredential);
    }

    this.pendingSocialCredential = undefined;
    this.pendingSocialMethod = undefined;
  }

  async confirmPasswordReset(code: string, password: string): Promise<void> {
    await this.initialize();
    if (isNativeApp()) {
      await FirebaseAuthentication.confirmPasswordReset({ oobCode: code, newPassword: password });
      return;
    }
    await confirmPasswordReset(this.requireWebAuth(), code, password);
  }

  async verifyPasswordResetCode(code: string): Promise<void> {
    await this.initialize();

    if (!this.auth) {
      this.auth = getAuth(this.getOrCreateApp());
      const config = environment.IDENTITY_PLATFORM as IdentityPlatformEnvironmentConfig;
      if (config.tenantId) this.auth.tenantId = config.tenantId;
      if (config.authEmulatorUrl) connectAuthEmulator(this.auth, config.authEmulatorUrl, { disableWarnings: true });
    }

    await verifyPasswordResetCode(this.auth, code);
  }

  async logout(): Promise<void> {
    await this.initialize();

    if (isNativeApp()) {
      await FirebaseAuthentication.signOut();
      this.nativeSession = false;
      this.pendingSocialCredential = undefined;
      this.pendingSocialMethod = undefined;
      return;
    }

    if (this.auth) await signOut(this.auth);
    this.pendingSocialCredential = undefined;
    this.pendingSocialMethod = undefined;
  }

  private async initializeSession(): Promise<void> {
    const identityPlatform = environment.IDENTITY_PLATFORM as IdentityPlatformEnvironmentConfig | undefined;
    this.validateConfiguration();

    if (isNativeApp()) {
      // The tenant is only configured for real Identity Platform projects
      // (dev/prod). The local flow targets the Firebase Auth emulator and stays
      // on the default tenant, so the tenant is optional here.
      const tenantId = identityPlatform?.tenantId;
      const emulatorUrl = identityPlatform?.authEmulatorUrl;
      if (emulatorUrl) {
        const parsedEmulatorUrl = new URL(emulatorUrl);
        await FirebaseAuthentication.useEmulator({
          host: parsedEmulatorUrl.hostname,
          port: Number(parsedEmulatorUrl.port || 9099)
        });
      }

      if (tenantId) {
        await FirebaseAuthentication.setTenantId({ tenantId });
      }
      const result = await FirebaseAuthentication.getCurrentUser();
      this.nativeSession = !!result.user;
      return;
    }

    const config = identityPlatform;
    if (!config?.projectId || !config?.apiKey || !config?.appId) return;

    const app = this.getOrCreateApp();
    this.auth = getAuth(app);
    // Only tenant-scoped projects (dev/prod) set a tenant; the local emulator
    // flow stays on the default tenant. Never assign `undefined` — the Auth
    // session restore asserts `tenantId === user.tenantId` (null).
    if (config.tenantId) {
      this.auth.tenantId = config.tenantId;
    }

    if (config.authEmulatorUrl) {
      connectAuthEmulator(this.auth, config.authEmulatorUrl, { disableWarnings: true });
    }

    await this.auth.authStateReady();
  }

  private getOrCreateApp(): FirebaseApp {
    const existingApp = getApps().find(({ name }) => name === FIREBASE_APP_NAME);
    if (existingApp) return getApp(FIREBASE_APP_NAME);

    // The web branch of initializeSession() validates these fields before
    // calling getOrCreateApp().
    const config = environment.IDENTITY_PLATFORM as IdentityPlatformAppConfig;
    return initializeApp(
      {
        apiKey: config.apiKey,
        authDomain: config.authDomain,
        projectId: config.projectId,
        appId: config.appId
      },
      FIREBASE_APP_NAME
    );
  }

  private requireWebAuth(): Auth {
    if (!this.auth) throw new Error('Identity Platform is not configured.');
    return this.auth;
  }
}
